// src/pages/Exams/generate/hooks/useGenerationJob.ts
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExamGenApi } from '../client';
import type {
  Blueprint, JobPhase, JobRow, QuestionDraft, SaveResponse, RegenVariant, QuestionFlag,
} from '../types';
import { DEFAULT_BLUEPRINT } from '../types';

const POLL_MS = 2500;
const IN_FLIGHT: JobPhase[] = ['queued', 'fetching_chunks', 'generating', 'scoring'];

// A draft question plus its position in the server's output_questions array.
interface DraftItem {
  q: QuestionDraft;
  serverIndex: number;
}

const SESSION_KEY = 'exam_gen_active_job';

function readSessionJobId(): number | null {
  try {
    const v = sessionStorage.getItem(SESSION_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}

function writeSessionJobId(id: number | null) {
  try {
    if (id == null) sessionStorage.removeItem(SESSION_KEY);
    else sessionStorage.setItem(SESSION_KEY, String(id));
  } catch {
    // ignore
  }
}

export interface UseGenerationJobReturn {
  blueprint: Blueprint;
  patchBlueprint: (patch: Partial<Blueprint>) => void;
  resetBlueprint: () => void;
  loadBlueprint: (bp: Blueprint) => void;

  jobId: number | null;
  job: JobRow | null;
  phase: JobPhase | 'idle';
  isPolling: boolean;
  /** True when the job fetch itself failed (404 for another user's job, network error). */
  loadError: boolean;

  startGeneration: () => Promise<void>;
  cancelGeneration: () => Promise<void>;
  saveAsExamSet: (name: string) => Promise<SaveResponse>;

  // Review-draft layer (Plan C)
  draftQuestions: QuestionDraft[];
  updateQuestion: (index: number, patch: Partial<QuestionDraft>) => void;
  removeQuestion: (index: number) => void;
  undoRemove: () => void;
  canUndoRemove: boolean;
  regenerateQuestion: (index: number, variant: RegenVariant) => Promise<QuestionDraft>;
  retryChapter: (formLabel: string, chapterId: number) => Promise<{ replaced: number }>;
  toggleFlag: (index: number, flag: QuestionFlag) => void;
  bulkRegenerate: (indices: number[], variant: RegenVariant) => Promise<void>;
  isBulkRegenerating: boolean;
  lastRegen: { index: number; prev: QuestionDraft } | null;
  undoRegenerate: () => void;
  clearLastRegen: () => void;

  startError: string | null;
  saveError: string | null;
  saveConflict: boolean;
}

export function useGenerationJob(
  initialJobId?: number | null,
  cycleId?: number | null,
): UseGenerationJobReturn {
  const qc = useQueryClient();
  const [blueprint, setBlueprint] = useState<Blueprint>(DEFAULT_BLUEPRINT);
  const [jobId, setJobId] = useState<number | null>(
    () => initialJobId ?? readSessionJobId());
  const [startError, setStartError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveConflict, setSaveConflict] = useState(false);

  // Review-draft layer (Plan C). Seeded once when a job reaches review_pending.
  // Each draft entry carries its index in the server's output_questions array.
  // The backend regenerate endpoint is positional, but local remove/undo reorder
  // the draft WITHOUT touching the server array — so serverIndex is the stable
  // address we must send for regenerate, not the (shifting) draft position.
  const [draftItems, setDraftItems] =
    useState<DraftItem[]>([]);
  const draftQuestions = useMemo(() => draftItems.map(it => it.q), [draftItems]);
  const [lastRemoved, setLastRemoved] =
    useState<{ index: number; item: DraftItem } | null>(null);
  const [lastRegen, setLastRegen] =
    useState<{ index: number; prev: QuestionDraft } | null>(null);
  const [isBulkRegenerating, setIsBulkRegenerating] = useState(false);
  // expectedRevision mirrors the server's job.revision; each successful
  // regenerate/retry bumps the server's revision by 1, so we bump this too.
  const [expectedRevision, setExpectedRevision] = useState<number>(0);
  const seededJobIdRef = useRef<number | null>(null);

  const patchBlueprint = useCallback((patch: Partial<Blueprint>) => {
    setBlueprint(prev => ({ ...prev, ...patch }));
  }, []);

  const resetBlueprint = useCallback(() => {
    setBlueprint(DEFAULT_BLUEPRINT);
  }, []);

  const loadBlueprint = useCallback((bp: Blueprint) => {
    setBlueprint(bp);
  }, []);

  const updateQuestion = useCallback((index: number, patch: Partial<QuestionDraft>) => {
    setDraftItems(prev => prev.map((it, i) => (i === index ? { ...it, q: { ...it.q, ...patch } } : it)));
  }, []);

  const toggleFlag = useCallback((index: number, flag: QuestionFlag) => {
    setDraftItems(prev => prev.map((it, i) => {
      if (i !== index) return it;
      const has = (it.q.flags ?? []).includes(flag);
      const flags = has ? (it.q.flags ?? []).filter(f => f !== flag)
                        : [...(it.q.flags ?? []), flag];
      return { ...it, q: { ...it.q, flags } };
    }));
  }, []);

  const removeQuestion = useCallback((index: number) => {
    setDraftItems(prev => {
      const it = prev[index];
      if (it) setLastRemoved({ index, item: it });
      return prev.filter((_, i) => i !== index);
    });
    // A remove shifts draft indices, which would make a pending regen-undo
    // target the wrong (or a since-shifted) question — drop it.
    setLastRegen(null);
  }, []);

  const undoRemove = useCallback(() => {
    setLastRemoved(prev => {
      if (!prev) return null;
      setDraftItems(cur => {
        const next = cur.slice();
        next.splice(Math.min(prev.index, next.length), 0, prev.item);
        return next;
      });
      return null;
    });
    // Undoing a remove re-shifts indices again, so any pending regen-undo is
    // stale for the same reason as above.
    setLastRegen(null);
  }, []);

  const jobQuery = useQuery({
    queryKey: ['exam_gen', 'job', jobId],
    queryFn: () => ExamGenApi.getJob(jobId!),
    enabled: !!jobId,
    // A 404 (another teacher's job, or a deleted one) never recovers: fail fast.
    retry: (count, err: any) => err?.response?.status !== 404 && count < 3,
    refetchInterval: (q) => {
      const data = q.state.data as JobRow | undefined;
      if (!data) return false;
      return IN_FLIGHT.includes(data.phase) ? POLL_MS : false;
    },
  });

  const job = jobQuery.data ?? null;
  const phase: JobPhase | 'idle' = job ? job.phase : 'idle';
  const isPolling = !!job && IN_FLIGHT.includes(job.phase);

  // Seed the editable draft exactly once per job when it enters review.
  useEffect(() => {
    if (!job) return;
    if (job.phase !== 'review_pending' && job.phase !== 'saved') return;
    if (seededJobIdRef.current === job.id) return;
    seededJobIdRef.current = job.id;
    setDraftItems((job.output_questions ?? []).map((q, i) => ({ q, serverIndex: i })));
    setExpectedRevision(job.revision ?? 0);
    setLastRemoved(null);
  }, [job]);

  // Persist active job id when it changes
  useEffect(() => {
    writeSessionJobId(jobId);
  }, [jobId]);

  useEffect(() => {
    if (initialJobId != null) setJobId(initialJobId);
  }, [initialJobId]);

  const createMut = useMutation({
    mutationFn: () => ExamGenApi.createJob(blueprint, cycleId ?? null),
    onSuccess: (data) => {
      setStartError(null);
      setJobId(data.job_id);
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
        ?? err?.response?.data
        ?? err?.message
        ?? 'Failed to start generation';
      setStartError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => ExamGenApi.cancelJob(jobId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exam_gen', 'job', jobId] });
    },
  });

  const saveMut = useMutation({
    mutationFn: (name: string) => {
      const formsMap = new Map<string, QuestionDraft[]>();
      draftQuestions.forEach((q) => {
        const lbl = q.form_label || 'A';
        if (!formsMap.has(lbl)) formsMap.set(lbl, []);
        formsMap.get(lbl)!.push(q);
      });
      const forms = Array.from(formsMap.entries()).map(([form_label, questions]) => ({
        form_label, questions,
      }));
      return ExamGenApi.saveJob(jobId!, {
        name,
        expected_revision: expectedRevision,
        forms,
      });
    },
    onSuccess: () => {
      setSaveError(null);
      setSaveConflict(false);
      writeSessionJobId(null);
      qc.invalidateQueries({ queryKey: ['exam_gen', 'job', jobId] });
      if (cycleId != null) {
        // Saving the exam set moves the cycle to "assigned" server-side; the
        // cycles list and the cycle detail both still hold the stale status.
        qc.invalidateQueries({ queryKey: ['school', 'cycles'] });
        qc.invalidateQueries({ queryKey: ['school', 'cycle', cycleId] });
      }
    },
    onError: (err: any) => {
      if (err?.response?.status === 409) {
        setSaveConflict(true);
        setSaveError('This job was updated elsewhere. Reload review.');
      } else {
        const detail = err?.response?.data?.detail
          ?? err?.message ?? 'Save failed';
        setSaveError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
    },
  });

  const regenerateMut = useMutation({
    mutationFn: (vars: { index: number; variant: RegenVariant }) => {
      // Address the backend by the question's stable server index, not its
      // (possibly shifted) draft position.
      const serverIndex = draftItems[vars.index]?.serverIndex ?? vars.index;
      return ExamGenApi.regenerateQuestion(jobId!, serverIndex, vars.variant);
    },
    onSuccess: (newQ, vars) => {
      // setLastRegen is invoked inside the setDraftItems updater to read the
      // pre-regen draft atomically, mirroring the existing lastRemoved idiom
      // above (removeQuestion) for consistency within this hook.
      setDraftItems(prev => {
        const prevQ = prev[vars.index]?.q;
        if (prevQ) setLastRegen({ index: vars.index, prev: prevQ });
        return prev.map((it, i) => (i === vars.index ? { ...it, q: newQ } : it));
      });
      setExpectedRevision(r => r + 1); // server did revision += 1
    },
  });

  // retry-chapter rewrites the whole chapter/form on the server and returns only
  // a count, so we refetch the job and re-seed the draft from authoritative data.
  const retryMut = useMutation({
    mutationFn: (vars: { formLabel: string; chapterId: number }) =>
      ExamGenApi.retryChapter(jobId!, vars.formLabel, vars.chapterId),
    onSuccess: async () => {
      const fresh = await ExamGenApi.getJob(jobId!);
      qc.setQueryData(['exam_gen', 'job', jobId], fresh);
      seededJobIdRef.current = null;           // force re-seed effect
      setDraftItems((fresh.output_questions ?? []).map((q, i) => ({ q, serverIndex: i })));
      setExpectedRevision(fresh.revision ?? 0);
      setLastRemoved(null);
    },
  });

  const startGeneration = useCallback(async () => {
    await createMut.mutateAsync();
  }, [createMut]);

  const cancelGeneration = useCallback(async () => {
    if (!jobId) return;
    await cancelMut.mutateAsync();
  }, [cancelMut, jobId]);

  const saveAsExamSet = useCallback(async (name: string) => {
    if (!jobId) throw new Error('No job to save');
    return saveMut.mutateAsync(name);
  }, [saveMut, jobId]);

  const regenerateQuestion = useCallback(
    (index: number, variant: RegenVariant) =>
      regenerateMut.mutateAsync({ index, variant }),
    [regenerateMut]);

  const retryChapter = useCallback(
    (formLabel: string, chapterId: number) =>
      retryMut.mutateAsync({ formLabel, chapterId }),
    [retryMut]);

  const undoRegenerate = useCallback(() => {
    setLastRegen(prev => {
      if (!prev) return null;
      setDraftItems(cur => cur.map((it, i) =>
        (i === prev.index ? { ...it, q: prev.prev } : it)));
      return null;
    });
  }, []);

  const clearLastRegen = useCallback(() => setLastRegen(null), []);

  const bulkRegenerate = useCallback(async (indices: number[], variant: RegenVariant) => {
    setIsBulkRegenerating(true);
    try {
      // ascending order; regeneration replaces in place so indices stay stable
      for (const i of [...indices].sort((a, b) => a - b)) {
        await regenerateMut.mutateAsync({ index: i, variant });
      }
    } finally {
      setIsBulkRegenerating(false);
      setLastRegen(null); // bulk regen is not single-undoable
    }
  }, [regenerateMut]);

  return {
    blueprint, patchBlueprint, resetBlueprint, loadBlueprint,
    jobId, job, phase, isPolling,
    loadError: jobQuery.isError && !jobQuery.data,
    startGeneration, cancelGeneration, saveAsExamSet,
    draftQuestions, updateQuestion, removeQuestion, undoRemove,
    canUndoRemove: !!lastRemoved,
    regenerateQuestion, retryChapter,
    toggleFlag, bulkRegenerate, isBulkRegenerating,
    lastRegen, undoRegenerate, clearLastRegen,
    startError, saveError, saveConflict,
  };
}
